import { Component, EventEmitter, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Subject, takeUntil } from 'rxjs';
import { DOC_STATE, DOC_UPLOAD } from 'src/app/constants/endpoint.const';
import { BaseService } from 'src/app/services/base.service';

@Component({
  selector: 'app-ca-submission',
  templateUrl: './ca-submission.component.html',
  styleUrls: ['./ca-submission.component.scss'],
  standalone: false
})
export class CaSubmissionComponent {
  loading: boolean = true;
  destroy$ = new Subject();
  caFileForm!: FormGroup;
  fileUploaded: { name: string; path?: string } | null = null;
  isEmpty: boolean = false;
  isConfirmBack: boolean = false
  @Output() openModalConfirmBack = new EventEmitter<boolean>();
  serviceType: string = '';
  serviceId: number = 0;

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private message: NzMessageService,
    private baseService: BaseService,
    private route: ActivatedRoute,
  ) { }

  ngOnInit(): void {
    window.scrollTo(0, 0);
    this.route.queryParams.subscribe((params) => {
      this.serviceType = params['service_type'];
    });
    this.serviceId = this.serviceType === 'shipping' ? 1 : 2;
    this.initForm();
    this.getExistingFile();
    setTimeout(() => {
      this.loading = false;
    }, 1000);
  }

  handleBack(): void {
    this.isConfirmBack = true
  }

  closeConfirmBack() {
    this.isConfirmBack = false
  }

  initForm(): void {
    this.caFileForm = this.fb.group({
      ca_file: [null, Validators.required],
    });
  }

  triggerFileInput(): void {
    const fileInput: HTMLInputElement = document.getElementById(
      'fileupload_ca_file'
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  deleteFile(): void {
    if (this.isEmpty) {
      this.fileUploaded = null;
      this.caFileForm.get('ca_file')?.setValue(null);
    } else {
      this.baseService.deleteData(DOC_UPLOAD + '/collaborator_agreement_file?service_types_id=' + this.serviceId)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.message.success('File deleted successfully.');
          this.fileUploaded = null;
          this.caFileForm.get('ca_file')?.setValue(null);
          this.getExistingFile();
        });
    }
  }


  handleFileUpload(event: any): void {
    const file: File = event.target.files[0];

    if (file) {
      if (file.type !== 'application/pdf') {
        this.message.create('error', 'Only PDF files are allowed.')
        this.deleteFile();
        return;
      }

      if (file.size > 5000 * 1024) {
        this.message.create('error', 'File size must not exceed 5MB.')
        this.deleteFile();
        return;
      }

      const fileURL = URL.createObjectURL(file);

      this.fileUploaded = {
        name: file.name,
        path: fileURL,
      };

      this.caFileForm.get('ca_file')?.setValue(file);
    }
  }

  getExistingFile(): void {
    this.baseService.getData(DOC_UPLOAD + '/collaborator_agreement_file?service_types_id=' + this.serviceId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        if (res.data) {
          const { file_name, uploaded_file_path } = res.data;

          this.fileUploaded = {
            name: file_name,
            path: uploaded_file_path,
          };
        }
      }, (err) => {
        this.isEmpty = err.error.data === null;
      });
  }
  uploadFile(): void {
    const formData = new FormData();
    formData.append('document_type', 'collaborator_agreement_file');
    formData.append('notes', '');
    formData.append('service_types_id', this.serviceId.toString());

    if (this.caFileForm.valid) {
      formData.append('file', this.caFileForm.get('ca_file')?.value);
    }

    this.baseService.postData(DOC_UPLOAD, formData)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.message.success('CA Document uploaded successfully.');
        this.updateState();
      });
  }

  getPreviewLink(file: { name: string; path?: string }): string {
    if (file && file.path) {
      if (file.path.endsWith('.pdf')) {
        return file.path;
      }
      else {
        const blob = new Blob([file.path], { type: 'text/plain' });
        return URL.createObjectURL(blob);
      }
    }
    return '#';
  }


  updateState(): void {
    const params = {
      document_type: 'CA',
      state: 4,
      service_types_id: this.serviceId,
    };
    this.baseService.putData(DOC_STATE, params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.router.navigate(['/document']);
        },
        error: (err) => {
          this.message.error(err.error.meta.message || 'Failed to update document state');
        }
      });
  }
}
